import { useState } from 'react';
import { C } from '@/lib/design';

type TooltipPosition = 'right' | 'left' | 'top' | 'bottom';

interface InfoTooltipProps {
  text: string;
  title?: string;
  position?: TooltipPosition;
  width?: number;
}

const PANEL_BASE: React.CSSProperties = {
  position: 'absolute',
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderLeft: `3px solid ${C.celeste}`,
  borderRadius: 8,
  boxShadow: '0 8px 32px rgba(31,56,100,0.14), 0 2px 8px rgba(0,0,0,0.06)',
  zIndex: 1000,
  animation: 'via-tooltip-fade-in 120ms ease-out forwards',
  overflow: 'hidden',
};

function panelPosition(pos: TooltipPosition, width: number): React.CSSProperties {
  switch (pos) {
    case 'left':
      return { right: 28, top: -6, width };
    case 'top':
      return { bottom: 24, left: -width / 2 + 8, width };
    case 'bottom':
      return { top: 24, left: -width / 2 + 8, width };
    case 'right':
    default:
      return { left: 28, top: -6, width };
  }
}

function caretStyle(pos: TooltipPosition): React.CSSProperties {
  const base: React.CSSProperties = { position: 'absolute', width: 0, height: 0 };
  switch (pos) {
    case 'left':
      return {
        ...base,
        right: -7,
        top: 14,
        border: '6px solid transparent',
        borderLeftColor: C.celeste,
      };
    case 'top':
      return {
        ...base,
        bottom: -7,
        left: 'calc(50% - 6px)',
        border: '6px solid transparent',
        borderTopColor: C.celeste,
      };
    case 'bottom':
      return {
        ...base,
        top: -7,
        left: 'calc(50% - 6px)',
        border: '6px solid transparent',
        borderBottomColor: C.celeste,
      };
    case 'right':
    default:
      return {
        ...base,
        left: -7,
        top: 14,
        border: '6px solid transparent',
        borderRightColor: C.celeste,
      };
  }
}

export function InfoTooltip({ text, title, position = 'right', width = 320 }: InfoTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [hover, setHover] = useState(false);

  const responsiveWidth =
    typeof window !== 'undefined' ? Math.min(width, window.innerWidth - 48) : width;

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        tabIndex={0}
        role="button"
        aria-label={title ?? 'Mas informacion'}
        aria-expanded={visible}
        onClick={(e) => {
          e.stopPropagation();
          setVisible((v) => !v);
        }}
        onMouseEnter={() => {
          setVisible(true);
          setHover(true);
        }}
        onMouseLeave={() => {
          setVisible(false);
          setHover(false);
        }}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setVisible((v) => !v);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setVisible(false);
            (e.currentTarget as HTMLSpanElement).blur();
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: `1.5px solid ${hover ? C.azul1 : C.azul2}`,
          background: hover ? '#EFF6FF' : 'transparent',
          color: hover ? C.azul1 : C.azul2,
          fontSize: 10,
          fontWeight: 700,
          cursor: 'pointer',
          marginLeft: 8,
          flexShrink: 0,
          userSelect: 'none',
          fontFamily: 'DM Sans, sans-serif',
          lineHeight: 1,
          transform: hover ? 'scale(1.1)' : 'scale(1)',
          transition: 'all 150ms ease',
          outlineOffset: 2,
        }}
      >
        i
      </span>

      {visible && (
        <span role="tooltip" style={{ ...PANEL_BASE, ...panelPosition(position, responsiveWidth) }}>
          <span style={caretStyle(position)} />
          {title && (
            <span
              style={{
                display: 'block',
                background: `linear-gradient(135deg, ${C.azul1} 0%, ${C.azul2} 100%)`,
                color: '#FFFFFF',
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.3,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {title}
            </span>
          )}
          <span
            style={{
              display: 'block',
              padding: '10px 14px',
              fontSize: 12,
              color: '#374151',
              lineHeight: 1.65,
              whiteSpace: 'pre-line',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 400,
            }}
          >
            {text}
          </span>
        </span>
      )}
    </span>
  );
}
